// Lookups для cash-desk форм: поиск клиентов + список шаблонов абонементов.
// Это read-only обёртки над общими эндпоинтами (clients / subscription-templates).
// Держим локально в cash-фиче, чтобы не зависеть от внутренних API других фич.

import { api } from '@/lib/api';

export interface ClientLookupItem {
  id: string;
  firstName: string;
  lastName: string;
  middleName: string | null;
  phone: string | null;
  /** Тип карточки — чтобы вызывающий мог прятать «представителей» из выбора. */
  clientType: 'client' | 'representative' | 'client_representative';
}

interface RawClientsResponse {
  items: ClientLookupItem[];
  nextCursor: string | null;
}

/**
 * @param excludeMerged скрыть объединённые (архивные) карточки — план 70.
 *   ВЫКЛЮЧЕН по умолчанию намеренно: этот же хелпер зовёт детектор дублей в
 *   форме создания клиента, и ему объединённые НУЖНЫ (иначе по номеру никто не
 *   найдётся и заведётся новый дубль). Включают там, где карточку ВЫБИРАЮТ:
 *   запись на занятие, касса, запись по карте, создание серии.
 */
export function searchClients(
  q: string,
  branchId?: string,
  excludeMerged?: boolean,
): Promise<ClientLookupItem[]> {
  const sp = new URLSearchParams();
  // Обрезаем до серверного лимита (`ListClientsQuery.q` — 120 символов). Иначе
  // вставка длинного текста в поиск даёт 422 и красное «Запрос не прошёл
  // проверку данных» вместо «ничего не найдено»: на проде 30.08 человек
  // вставил в поле расписание группы на 300 символов. Для поиска первых 120
  // символов с запасом достаточно.
  if (q) sp.set('q', q.slice(0, 120));
  // Опциональный фильтр по филиалу (list-clients.query поддерживает branch_id).
  if (branchId) sp.set('branch_id', branchId);
  if (excludeMerged) sp.set('exclude_merged', 'true');
  sp.set('limit', '10');
  const qs = sp.toString();
  return api.get<RawClientsResponse>(`/clients?${qs}`).then((r) => r.items);
}

export type SubscriptionTemplateType =
  | 'single'
  | 'period_with_count'
  | 'unlimited_period'
  | 'complex'
  | 'personal'
  | 'wedding_choreo'
  | 'rental'
  | 'trial';

export interface SubscriptionTemplateItem {
  id: string;
  name: string;
  type: SubscriptionTemplateType;
  price_kopecks: number;
  sessions_count: number | null;
  duration_days: number | null;
  /**
   * Режим срока. `calendar_month` — абонемент живёт до конца месяца активации,
   * число занятий и цена считаются при продаже (план 101 Ф1).
   */
  validity_mode: 'rolling' | 'calendar_month';
  /** Цена за занятие календарного товара; null — у остальных и у безлимита. */
  price_per_session_kopecks: number | null;
  allow_debt: boolean;
  status: 'active' | 'archived';
  /** Группы товара: из них строятся выдачи безлимита (план 158 Ф1). */
  group_ids: string[];
}

/**
 * Прайс для продажи. `branchId` — филиал ОТКРЫТОЙ СМЕНЫ, не шапки: продажа
 * принадлежит смене, и продавать в Мурманске московский абонемент нельзя
 * (план 80 Ф5.1). Сервер сам пропускает товары без привязок — они общие.
 */
export function listSubscriptionTemplates(branchId?: string): Promise<SubscriptionTemplateItem[]> {
  const qs = branchId ? `?branch_id=${encodeURIComponent(branchId)}` : '';
  return api
    .get<{ data: SubscriptionTemplateItem[] }>(`/subscription-templates${qs}`)
    .then((r) => r.data.filter((t) => t.status === 'active'));
}
